import { AGREEMENT_DOCUMENT_VERSION, AGREEMENT_V2_DOCUMENT_VERSION, AGREEMENT_V3_DOCUMENT_VERSION, AGREEMENT_V4_DOCUMENT_VERSION, AGREEMENT_V5_DOCUMENT_VERSION, additionalServiceComplete, agreementIsSingleLanguage, agreementSigned, agreementTextVersion, agreementVersionAtLeast, agreementVersionLabel, type V2AdditionalService, type V2Agreement } from '../domain/agreement';
import {
  AGREEMENT_V5_CONSENT_BODY_EN,
  AGREEMENT_V5_CONSENT_BODY_ZH,
  AGREEMENT_V5_CONSENT_POINTS_EN,
  AGREEMENT_V5_CONSENT_POINTS_ZH,
  AGREEMENT_V5_ESIGN_BODY_EN,
  AGREEMENT_V5_ESIGN_BODY_ZH,
  AGREEMENT_V5_ESIGN_CONFIRM_EN,
  AGREEMENT_V5_ESIGN_CONFIRM_ZH,
  AGREEMENT_V5_ESIGN_POINTS_EN,
  AGREEMENT_V5_ESIGN_POINTS_ZH,
  AGREEMENT_V5_CLAIMS_CHECKBOX_EN,
  AGREEMENT_V5_CLAIMS_CHECKBOX_ZH,
  AGREEMENT_V5_CLAIMS_POINTS_EN,
  AGREEMENT_V5_CLAIMS_POINTS_ZH,
  AGREEMENT_V5_CLAIMS_TITLE_EN,
  AGREEMENT_V5_CLAIMS_TITLE_ZH,
  AGREEMENT_V5_CLAUSE_NOTE_ZH,
  AGREEMENT_V5_CLAUSES_ORIGINAL_NOTE_ZH,
  AGREEMENT_V5_CLAUSES_ORIGINAL_TITLE_ZH,
  AGREEMENT_V5_CONSENT_BASIS_ZH,
  AGREEMENT_V5_NEW_BUILD_DECLARATION_ZH,
  AGREEMENT_V5_NEW_BUILD_LICENSE_NOTE_ZH,
  AGREEMENT_V5_NY_MANDATORY_CLAUSES_ZH,
  AGREEMENT_V5_STATE_LINE_ZH,
  AGREEMENT_V5_RECORDS_EN,
  AGREEMENT_V5_RECORDS_ZH,
  AGREEMENT_V5_SCOPE_BODY_EN,
  AGREEMENT_V5_SCOPE_BODY_ZH,
  AGREEMENT_V5_SCOPE_OPENING_EN,
  AGREEMENT_V5_SCOPE_OPENING_ZH,
  AGREEMENT_COPIES_EN,
  AGREEMENT_COPIES_ZH,
  AGREEMENT_ESIGN_BODY_EN,
  AGREEMENT_ESIGN_BODY_ZH,
  AGREEMENT_ESIGN_CHECKBOX_EN,
  AGREEMENT_ESIGN_CHECKBOX_ZH,
  AGREEMENT_ESIGN_CONFIRM_EN,
  AGREEMENT_ESIGN_CONFIRM_ZH,
  AGREEMENT_ESIGN_POINTS_EN,
  AGREEMENT_ESIGN_POINTS_ZH,
  AGREEMENT_ESIGN_TITLE_EN,
  AGREEMENT_ESIGN_TITLE_ZH,
  AGREEMENT_V4_CONSENT_BODY_EN,
  AGREEMENT_V4_CONSENT_BODY_ZH,
  AGREEMENT_V4_CONSENT_CHECKBOX_EN,
  AGREEMENT_V4_CONSENT_CHECKBOX_ZH,
  AGREEMENT_V4_CONSENT_POINTS_EN,
  AGREEMENT_V4_CONSENT_POINTS_ZH,
  AGREEMENT_V4_CONSENT_TITLE_EN,
  AGREEMENT_V4_CONSENT_TITLE_ZH,
  AGREEMENT_V4_RETENTION_EN,
  AGREEMENT_V4_RETENTION_ZH,
  withAdditionalServicesInBody,
  AGREEMENT_CLAUSE_READING_LABEL_ZH,
  AGREEMENT_CONSENT_BASIS_EN,
  AGREEMENT_CONSENT_BASIS_ZH,
  AGREEMENT_CONSENT_BODY_EN,
  AGREEMENT_CONSENT_BODY_ZH,
  AGREEMENT_CONSENT_CHECKBOX_EN,
  AGREEMENT_CONSENT_CHECKBOX_ZH,
  AGREEMENT_CONSENT_POINTS_EN,
  AGREEMENT_CONSENT_POINTS_ZH,
  AGREEMENT_CONSENT_TITLE_EN,
  AGREEMENT_CONSENT_TITLE_ZH,
  AGREEMENT_CREDENTIAL_LINE_EN,
  AGREEMENT_CREDENTIAL_LINE_ZH,
  AGREEMENT_LANGUAGE_PRECEDENCE_EN,
  AGREEMENT_LANGUAGE_PRECEDENCE_ZH,
  AGREEMENT_LICENSE_LABEL_EN,
  AGREEMENT_LICENSE_LABEL_ZH,
  AGREEMENT_NEW_BUILD_CLAUSES,
  AGREEMENT_NEW_BUILD_DECLARATION_EN,
  AGREEMENT_NEW_BUILD_DECLARATION_ZH,
  AGREEMENT_NEW_BUILD_LICENSE_NOTE_EN,
  AGREEMENT_NEW_BUILD_LICENSE_NOTE_ZH,
  AGREEMENT_NEW_BUILD_TITLE_EN,
  AGREEMENT_NEW_BUILD_TITLE_ZH,
  AGREEMENT_NY_MANDATORY_CLAUSES,
  AGREEMENT_NY_MANDATORY_CLAUSES_READING_ZH,
  AGREEMENT_RETENTION_EN,
  AGREEMENT_RETENTION_ZH,
  AGREEMENT_SCOPE_OPENING_EN,
  AGREEMENT_SCOPE_OPENING_ZH,
  AGREEMENT_SOP_CITATION,
  AGREEMENT_STATE_NAME_EN,
  AGREEMENT_STATE_NAME_ZH,
  AGREEMENT_V1_SCOPE_BODY_EN,
  AGREEMENT_V1_SCOPE_BODY_ZH,
  AGREEMENT_V2_SCOPE_BODY_EN,
  AGREEMENT_V2_SCOPE_BODY_ZH
} from '../domain/agreementText';
import { displayAddress, type V2Inspection } from '../domain/inspection';
import { inspectionServiceOf, type InspectionService } from '../domain/inspectionService';
import { signatureSvg } from '../domain/signature';
import { translate, type MessageKey } from '../i18n/i18n';
import type { AppLocale } from '../i18n/locales';
import { flowHardWrappedText } from './flowText';
import { demoWatermarkStyle } from './watermark';

// Everything the fixed document needs from an inspection (the details screen passes a draft copy).
export type AgreementSource = Pick<V2Inspection, 'property' | 'client' | 'inspector' | 'inspectionAt' | 'agreement'>;

// The Inspection Agreement document (Owner task book 2026-09-04 Round 2 §C / §AK): ONE renderer from
// the inspection record to HTML, used by the in-app "View agreement" sheet and — through the same
// device-side PDF rasterizer as the report — for the agreement's OWN PDF. The wording is the approved
// document (src/domain/agreementText.ts): scope verbatim in each language, the New York mandatory
// clauses always in their English legal text, then the two hand-drawn signatures. It never touches
// the report and the report never rewrites it.
// NY F1 Round 5 (OD-8): the consent clause is its own section, immediately before the signatures,
// and shows what the client actually decided (☑ / ☐) — plus, when consent was withdrawn in writing
// afterwards, a line saying so, because the signed document itself is never rewritten.
//
// Owner, TestFlight device test 2026-09-12 — the document the client signs is BILINGUAL, whatever the
// App Language: the complete Chinese version on top, the complete English version below, then one set
// of signatures. The agreement goes to the client's attorney like the report does, so English is the
// text that stands; it is signed with the client, so the inspector and a Chinese-speaking client read
// it in Chinese first. The two New York mandatory clauses stay the English legal text in both versions
// (19 NYCRR 197-4.2(a)); from the copy of 2026-09-12 the Chinese version adds a reading aid under each,
// and each version closes with the language-precedence clause (English governs). This is the
// agreement only: the
// report still comes in English OR Chinese (tests/guards/ownerTestProduct.guard.test.ts).
//
// Owner 2026-10-28 replaced that for documents signed from then on (copy v5): the agreement is the
// client's, so it comes in the ONE language chosen for the client — Chinese for a client who asks for
// Chinese, English otherwise (V2Agreement.documentLanguage) — and the Chinese document is Chinese
// throughout, the mandatory clauses included (「协议不要这样一段中文，一段英文」). Documents signed under an
// older copy keep printing bilingual (renderAgreementDocumentBody picks).
export interface AgreementConsentBlock {
  title: string;
  basis: string;
  body: string;
  points: readonly string[];
  checkbox: string;
  given: boolean;
  // v4: "Ticked by the Client: <time>" under the box. null for older copies or an unticked box.
  givenNote: string | null;
  withdrawnNote: string | null;
}

// v5: the limitation of liability and claims terms with the Client's own tick. null for older copies.
export interface AgreementClaimsBlock {
  title: string;
  points: readonly string[];
  checkbox: string;
  given: boolean;
  givenNote: string | null;
}

// v4 §3: consent to electronic records and signatures (15 U.S.C. §7001(c)). null for older copies.
export interface AgreementEsignBlock {
  title: string;
  body: string;
  points: readonly string[];
  confirm: string;
  checkbox: string;
  given: boolean;
  givenNote: string | null;
}

export interface AgreementModel {
  locale: AppLocale;
  // OD-9: the ordinary home inspection agreement, or the New Build Visual Observation Agreement.
  service: InspectionService;
  demo: boolean;
  signed: boolean;
  title: string;
  stateLine: string;
  versionLine: string;
  details: Array<{ key: string; value: string }>;
  scope: string;
  clauses: string[];
  clauseNote: string;
  // The copy of 2026-09-12 onward, Chinese version only: a reading aid under each clause (index-aligned with
  // `clauses`) and its label. null = the English clauses alone.
  clauseReadings: { label: string; texts: string[] } | null;
  // The copy of 2026-10-28 onward: the limitation of liability and claims terms.
  claims: AgreementClaimsBlock | null;
  // null for a document signed under the 2026-08-25 copy, which carried neither clause.
  consent: AgreementConsentBlock | null;
  retention: string | null;
  // The copy of 2026-10-19 onward: consent to electronic records and signatures, and how the Client
  // receives copies. null for the older copies.
  esign: AgreementEsignBlock | null;
  copies: string | null;
  // The copy of 2026-09-12 onward: which language governs. null for the older copies.
  languagePrecedence: string | null;
  // v5, Chinese document only: the mandatory clauses in the regulation's English words, together at the end
  // (the document's own clause section is Chinese). null everywhere else.
  clausesOriginal: { title: string; note: string; texts: string[] } | null;
  // New Build only (approved draft §4.3): the licence number in the signature area, with the note
  // that this engagement is not a licensed home inspection under Article 12-B.
  license: { label: string; number: string; note: string } | null;
  // remoteNote: the Client signed through a one-time link (when the server received it, from which address).
  client: { label: string; name: string; svg: string | null; signedAt: string | null; remoteNote?: string | null };
  inspector: { label: string; name: string; svg: string | null; signedAt: string | null };
  labels: Record<'details' | 'scope' | 'clauses' | 'signatures' | 'notSigned' | 'signedAt' | 'demoBanner' | 'demoWatermark' | 'languageVersion', string>;
}

// The two language versions of ONE document: the same record, the same generation time.
export interface BilingualAgreementModels {
  zh: AgreementModel;
  en: AgreementModel;
}

function formatDateTime(locale: AppLocale, ts: number): string {
  try {
    return new Date(ts).toLocaleString(locale, { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch {
    return new Date(ts).toISOString();
  }
}

// The INCLUDED / NOT INCLUDED body of the approved copy a document carries. A version this build
// does not know reads as the current copy (the text the signing sheet presents).
export function agreementScopeBodyFor(locale: AppLocale, version: string = AGREEMENT_DOCUMENT_VERSION): string {
  const zh = locale === 'zh-CN';
  if (agreementVersionAtLeast(version, AGREEMENT_V5_DOCUMENT_VERSION)) return zh ? AGREEMENT_V5_SCOPE_BODY_ZH : AGREEMENT_V5_SCOPE_BODY_EN;
  if (!agreementVersionAtLeast(version, AGREEMENT_V2_DOCUMENT_VERSION) && version !== AGREEMENT_DOCUMENT_VERSION) {
    return zh ? AGREEMENT_V1_SCOPE_BODY_ZH : AGREEMENT_V1_SCOPE_BODY_EN;
  }
  return zh ? AGREEMENT_V2_SCOPE_BODY_ZH : AGREEMENT_V2_SCOPE_BODY_EN;
}

// The whole scope section: the opening sentence — replaced by the New Build declaration in that
// variant (approved draft §4.2) — followed by the body of the copy in force. From v4 the additional
// services agreed for this property replace "None." (only complete rows — an incomplete one blocks
// signing, so it never reaches a signed document).
export function agreementScopeBodyWithServicesFor(locale: AppLocale, version: string, additionalServices: readonly V2AdditionalService[] = []): string {
  const services = agreementVersionAtLeast(version, AGREEMENT_V4_DOCUMENT_VERSION) ? additionalServices.filter(additionalServiceComplete).map((service) => ({ name: service.name.trim(), fee: service.fee.trim() })) : [];
  return withAdditionalServicesInBody(agreementScopeBodyFor(locale, version), locale === 'zh-CN' ? 'zh-CN' : 'en-US', services);
}

export function scopeTextFor(locale: AppLocale, options: { version?: string; service?: InspectionService; additionalServices?: readonly V2AdditionalService[] } = {}): string {
  const zh = locale === 'zh-CN';
  const version = options.version ?? AGREEMENT_DOCUMENT_VERSION;
  const body = agreementScopeBodyWithServicesFor(locale, version, options.additionalServices);
  const v5 = agreementVersionAtLeast(version, AGREEMENT_V5_DOCUMENT_VERSION);
  if (options.service === 'newBuild') return `${zh ? (v5 ? AGREEMENT_V5_NEW_BUILD_DECLARATION_ZH : AGREEMENT_NEW_BUILD_DECLARATION_ZH) : AGREEMENT_NEW_BUILD_DECLARATION_EN}\n\n${body}`;
  if (v5) return `${zh ? AGREEMENT_V5_SCOPE_OPENING_ZH : AGREEMENT_V5_SCOPE_OPENING_EN}\n\n${body}`;
  return `${zh ? AGREEMENT_SCOPE_OPENING_ZH : AGREEMENT_SCOPE_OPENING_EN}\n\n${body}`;
}

// The approved title of the document this agreement is (OD-9 / §4.1). Never "Inspection Agreement"
// for a New Build observation.
export function agreementTitleFor(locale: AppLocale, service: InspectionService): string {
  if (service === 'newBuild') return locale === 'zh-CN' ? AGREEMENT_NEW_BUILD_TITLE_ZH : AGREEMENT_NEW_BUILD_TITLE_EN;
  return translate(locale, 'agreement.documentTitle');
}

// The consent and records-and-retention clauses of the copy in force, for the signing sheet and the
// Settings › Terms page as well as the document itself. null = a copy that carries neither.
export function agreementConsentTextFor(locale: AppLocale, version: string = AGREEMENT_DOCUMENT_VERSION): Omit<AgreementConsentBlock, 'given' | 'givenNote' | 'withdrawnNote'> | null {
  if (!agreementVersionAtLeast(version, AGREEMENT_V2_DOCUMENT_VERSION)) return null;
  const zh = locale === 'zh-CN';
  // v4 widens the clause to cloud storage of the final PDFs; the basis line is unchanged. v5 says the same
  // facts in fewer words, under the same title and tick sentence.
  if (agreementVersionAtLeast(version, AGREEMENT_V4_DOCUMENT_VERSION)) {
    const v5 = agreementVersionAtLeast(version, AGREEMENT_V5_DOCUMENT_VERSION);
    return {
      title: zh ? AGREEMENT_V4_CONSENT_TITLE_ZH : AGREEMENT_V4_CONSENT_TITLE_EN,
      basis: zh ? (v5 ? AGREEMENT_V5_CONSENT_BASIS_ZH : AGREEMENT_CONSENT_BASIS_ZH) : AGREEMENT_CONSENT_BASIS_EN,
      body: v5 ? (zh ? AGREEMENT_V5_CONSENT_BODY_ZH : AGREEMENT_V5_CONSENT_BODY_EN) : zh ? AGREEMENT_V4_CONSENT_BODY_ZH : AGREEMENT_V4_CONSENT_BODY_EN,
      points: v5 ? (zh ? AGREEMENT_V5_CONSENT_POINTS_ZH : AGREEMENT_V5_CONSENT_POINTS_EN) : zh ? AGREEMENT_V4_CONSENT_POINTS_ZH : AGREEMENT_V4_CONSENT_POINTS_EN,
      checkbox: zh ? AGREEMENT_V4_CONSENT_CHECKBOX_ZH : AGREEMENT_V4_CONSENT_CHECKBOX_EN
    };
  }
  return {
    title: zh ? AGREEMENT_CONSENT_TITLE_ZH : AGREEMENT_CONSENT_TITLE_EN,
    basis: zh ? AGREEMENT_CONSENT_BASIS_ZH : AGREEMENT_CONSENT_BASIS_EN,
    body: zh ? AGREEMENT_CONSENT_BODY_ZH : AGREEMENT_CONSENT_BODY_EN,
    points: zh ? AGREEMENT_CONSENT_POINTS_ZH : AGREEMENT_CONSENT_POINTS_EN,
    checkbox: zh ? AGREEMENT_CONSENT_CHECKBOX_ZH : AGREEMENT_CONSENT_CHECKBOX_EN
  };
}

export function agreementRetentionTextFor(locale: AppLocale, version: string = AGREEMENT_DOCUMENT_VERSION): string | null {
  if (!agreementVersionAtLeast(version, AGREEMENT_V2_DOCUMENT_VERSION)) return null;
  // v5: records and retention and how the Client receives copies are one paragraph.
  if (agreementVersionAtLeast(version, AGREEMENT_V5_DOCUMENT_VERSION)) return locale === 'zh-CN' ? AGREEMENT_V5_RECORDS_ZH : AGREEMENT_V5_RECORDS_EN;
  if (agreementVersionAtLeast(version, AGREEMENT_V4_DOCUMENT_VERSION)) return locale === 'zh-CN' ? AGREEMENT_V4_RETENTION_ZH : AGREEMENT_V4_RETENTION_EN;
  return locale === 'zh-CN' ? AGREEMENT_RETENTION_ZH : AGREEMENT_RETENTION_EN;
}

// v4 §3 / §4: consent to electronic records and signatures, and how the Client receives copies.
export function agreementEsignTextFor(locale: AppLocale, version: string = AGREEMENT_DOCUMENT_VERSION): Omit<AgreementEsignBlock, 'given' | 'givenNote'> | null {
  if (!agreementVersionAtLeast(version, AGREEMENT_V4_DOCUMENT_VERSION)) return null;
  const zh = locale === 'zh-CN';
  // v5: the seven E-SIGN disclosures in four points, under the same title and tick sentence.
  if (agreementVersionAtLeast(version, AGREEMENT_V5_DOCUMENT_VERSION)) {
    return {
      title: zh ? AGREEMENT_ESIGN_TITLE_ZH : AGREEMENT_ESIGN_TITLE_EN,
      body: zh ? AGREEMENT_V5_ESIGN_BODY_ZH : AGREEMENT_V5_ESIGN_BODY_EN,
      points: zh ? AGREEMENT_V5_ESIGN_POINTS_ZH : AGREEMENT_V5_ESIGN_POINTS_EN,
      confirm: zh ? AGREEMENT_V5_ESIGN_CONFIRM_ZH : AGREEMENT_V5_ESIGN_CONFIRM_EN,
      checkbox: zh ? AGREEMENT_ESIGN_CHECKBOX_ZH : AGREEMENT_ESIGN_CHECKBOX_EN
    };
  }
  return {
    title: zh ? AGREEMENT_ESIGN_TITLE_ZH : AGREEMENT_ESIGN_TITLE_EN,
    body: zh ? AGREEMENT_ESIGN_BODY_ZH : AGREEMENT_ESIGN_BODY_EN,
    points: zh ? AGREEMENT_ESIGN_POINTS_ZH : AGREEMENT_ESIGN_POINTS_EN,
    confirm: zh ? AGREEMENT_ESIGN_CONFIRM_ZH : AGREEMENT_ESIGN_CONFIRM_EN,
    checkbox: zh ? AGREEMENT_ESIGN_CHECKBOX_ZH : AGREEMENT_ESIGN_CHECKBOX_EN
  };
}

// v5: the limitation of liability and claims terms (the signing sheet and the signing page show the tick).
export function agreementClaimsTextFor(locale: AppLocale, version: string = AGREEMENT_DOCUMENT_VERSION): Omit<AgreementClaimsBlock, 'given' | 'givenNote'> | null {
  if (!agreementVersionAtLeast(version, AGREEMENT_V5_DOCUMENT_VERSION)) return null;
  const zh = locale === 'zh-CN';
  return { title: zh ? AGREEMENT_V5_CLAIMS_TITLE_ZH : AGREEMENT_V5_CLAIMS_TITLE_EN, points: zh ? AGREEMENT_V5_CLAIMS_POINTS_ZH : AGREEMENT_V5_CLAIMS_POINTS_EN, checkbox: zh ? AGREEMENT_V5_CLAIMS_CHECKBOX_ZH : AGREEMENT_V5_CLAIMS_CHECKBOX_EN };
}

// v4 only: from v5 the copies sentence is part of the records paragraph.
export function agreementCopiesTextFor(locale: AppLocale, version: string = AGREEMENT_DOCUMENT_VERSION): string | null {
  if (!agreementVersionAtLeast(version, AGREEMENT_V4_DOCUMENT_VERSION) || agreementVersionAtLeast(version, AGREEMENT_V5_DOCUMENT_VERSION)) return null;
  return locale === 'zh-CN' ? AGREEMENT_COPIES_ZH : AGREEMENT_COPIES_EN;
}

// The language-precedence clause of the copy in force (2026-09-12 onward). null = a copy without one.
// v5 has one language, so no precedence clause.
export function agreementLanguagePrecedenceFor(locale: AppLocale, version: string = AGREEMENT_DOCUMENT_VERSION): string | null {
  if (!agreementVersionAtLeast(version, AGREEMENT_V3_DOCUMENT_VERSION) || agreementVersionAtLeast(version, AGREEMENT_V5_DOCUMENT_VERSION)) return null;
  return locale === 'zh-CN' ? AGREEMENT_LANGUAGE_PRECEDENCE_ZH : AGREEMENT_LANGUAGE_PRECEDENCE_EN;
}

// The Chinese reading aid for the clauses a document prints (the bilingual copies v3 / v4, Chinese version
// only), aligned with those clauses. The English clause is the legal text; this only helps it be read. The
// Chinese v5 document prints the clauses in Chinese instead, so it needs none.
export function agreementClauseReadingsFor(locale: AppLocale, service: InspectionService, version: string = AGREEMENT_DOCUMENT_VERSION): { label: string; texts: string[] } | null {
  if (locale !== 'zh-CN' || !agreementVersionAtLeast(version, AGREEMENT_V3_DOCUMENT_VERSION) || agreementVersionAtLeast(version, AGREEMENT_V5_DOCUMENT_VERSION)) return null;
  return { label: AGREEMENT_CLAUSE_READING_LABEL_ZH, texts: service === 'newBuild' ? [AGREEMENT_NY_MANDATORY_CLAUSES_READING_ZH[1]] : [...AGREEMENT_NY_MANDATORY_CLAUSES_READING_ZH] };
}

export function buildAgreementModel(inspection: AgreementSource, locale: AppLocale, generatedAt: number): AgreementModel {
  const t = (key: MessageKey, params?: Record<string, string | number>) => translate(locale, key, params);
  const zh = locale === 'zh-CN';
  const agreement: V2Agreement = inspection.agreement;
  // A SIGNED document prints the text it was signed under and the service it was signed for; an open
  // one prints the current copy for the service the property calls for.
  const version = agreementTextVersion(agreement);
  const service: InspectionService = agreement.signedService ?? inspectionServiceOf(inspection.property);
  const notRecorded = t('report.notRecorded');
  const details: AgreementModel['details'] = [
    { key: t('agreement.docProperty'), value: displayAddress(inspection, notRecorded) },
    { key: t('agreement.docClient'), value: inspection.client.name || agreement.client.name || notRecorded },
    { key: t('agreement.docInspectionDate'), value: formatDateTime(locale, inspection.inspectionAt) },
    { key: t('agreement.docInspector'), value: `${inspection.inspector.name || agreement.inspector.name || notRecorded} · ${zh ? AGREEMENT_CREDENTIAL_LINE_ZH : AGREEMENT_CREDENTIAL_LINE_EN}` },
    { key: t('agreement.docFee'), value: agreement.demo ? t('agreement.feeDemo') : agreement.fee.amount.trim().length > 0 ? `$${agreement.fee.amount.trim()}` : notRecorded }
  ];
  const consentText = agreementConsentTextFor(locale, version);
  const esignText = agreementEsignTextFor(locale, version);
  const claimsText = agreementClaimsTextFor(locale, version);
  const v4 = agreementVersionAtLeast(version, AGREEMENT_V4_DOCUMENT_VERSION);
  // v5, Chinese document: Chinese throughout, the mandatory clauses included (Owner 2026-10-28).
  const chineseThroughout = zh && agreementVersionAtLeast(version, AGREEMENT_V5_DOCUMENT_VERSION);
  const mandatory = chineseThroughout ? AGREEMENT_V5_NY_MANDATORY_CLAUSES_ZH : AGREEMENT_NY_MANDATORY_CLAUSES;
  const tickedNote = (given: boolean, at: number | null) => (v4 && given && at !== null ? t('agreement.tickedAt', { date: formatDateTime(locale, at) }) : null);
  const party = (label: string, p: V2Agreement['client']) => ({
    label,
    name: p.name,
    svg: p.signature && p.signature.strokes.length > 0 ? signatureSvg(p.signature, { width: 240, height: Math.round((240 * p.signature.height) / p.signature.width), className: 'ag-sig' }) : null,
    signedAt: p.signedAt !== null ? formatDateTime(locale, p.signedAt) : null
  });
  return {
    locale,
    service,
    demo: agreement.demo,
    signed: agreementSigned(agreement),
    title: agreementTitleFor(locale, service),
    stateLine: chineseThroughout ? AGREEMENT_V5_STATE_LINE_ZH : `${zh ? AGREEMENT_STATE_NAME_ZH : AGREEMENT_STATE_NAME_EN} · ${AGREEMENT_SOP_CITATION}`,
    versionLine: `${t('agreement.docVersion')}: ${agreementVersionLabel(version)} · ${t('report.generatedAt')}: ${formatDateTime(locale, generatedAt)}`,
    details,
    scope: scopeTextFor(locale, { version, service, additionalServices: agreement.additionalServices }),
    // §4.3: the New Build variant does not print the first mandatory clause — it states what a
    // licensed home inspector may report on under Article 12-B, which this service is not.
    clauses: chineseThroughout ? (service === 'newBuild' ? [mandatory[1]] : [...mandatory]) : [...(service === 'newBuild' ? AGREEMENT_NEW_BUILD_CLAUSES : AGREEMENT_NY_MANDATORY_CLAUSES)],
    clauseNote: chineseThroughout ? AGREEMENT_V5_CLAUSE_NOTE_ZH : t('agreement.clauseNote'),
    clauseReadings: agreementClauseReadingsFor(locale, service, version),
    claims: claimsText ? { ...claimsText, given: agreement.clientLiabilityAck, givenNote: tickedNote(agreement.clientLiabilityAck, agreement.clientLiabilityAckAt) } : null,
    consent: consentText
      ? {
          ...consentText,
          given: agreement.clientAiConsent,
          givenNote: tickedNote(agreement.clientAiConsent, agreement.clientAiConsentAt),
          withdrawnNote: agreement.clientAiConsent && agreement.aiConsentWithdrawnAt !== null ? t('agreement.consentWithdrawnOn', { date: formatDateTime(locale, agreement.aiConsentWithdrawnAt) }) : null
        }
      : null,
    retention: agreementRetentionTextFor(locale, version),
    esign: esignText ? { ...esignText, given: agreement.clientEsignConsent, givenNote: tickedNote(agreement.clientEsignConsent, agreement.clientEsignConsentAt) } : null,
    copies: agreementCopiesTextFor(locale, version),
    languagePrecedence: agreementLanguagePrecedenceFor(locale, version),
    clausesOriginal: chineseThroughout
      ? { title: AGREEMENT_V5_CLAUSES_ORIGINAL_TITLE_ZH, note: AGREEMENT_V5_CLAUSES_ORIGINAL_NOTE_ZH, texts: [...(service === 'newBuild' ? AGREEMENT_NEW_BUILD_CLAUSES : AGREEMENT_NY_MANDATORY_CLAUSES)] }
      : null,
    license:
      service === 'newBuild'
        ? {
            label: zh ? AGREEMENT_LICENSE_LABEL_ZH : AGREEMENT_LICENSE_LABEL_EN,
            number: inspection.inspector.licenseNumber.trim().length > 0 ? inspection.inspector.licenseNumber.trim() : notRecorded,
            note: zh ? (chineseThroughout ? AGREEMENT_V5_NEW_BUILD_LICENSE_NOTE_ZH : AGREEMENT_NEW_BUILD_LICENSE_NOTE_ZH) : AGREEMENT_NEW_BUILD_LICENSE_NOTE_EN
          }
        : null,
    client: {
      ...party(t('agreement.clientSignature'), agreement.client),
      remoteNote: agreement.clientRemote
        ? agreement.clientRemote.ip
          ? t('agreement.signedRemotely', { date: formatDateTime(locale, agreement.clientRemote.receivedAt), ip: agreement.clientRemote.ip })
          : t('agreement.signedRemotelyNoIp', { date: formatDateTime(locale, agreement.clientRemote.receivedAt) })
        : null
    },
    inspector: party(t('agreement.inspectorSignature'), agreement.inspector),
    labels: {
      details: t('agreement.sectionDetails'),
      scope: t('agreement.sectionScope'),
      clauses: t('agreement.sectionClauses'),
      signatures: t('agreement.sectionSignatures'),
      notSigned: t('agreement.notSigned'),
      signedAt: t('agreement.signedAtLabel'),
      demoBanner: t('report.demoBanner'),
      demoWatermark: t('report.demoWatermark'),
      languageVersion: t('agreement.languageVersion')
    }
  };
}

// The document the client signs: the Chinese and the English version of the same record.
export function buildBilingualAgreementModels(inspection: AgreementSource, generatedAt: number): BilingualAgreementModels {
  return { zh: buildAgreementModel(inspection, 'zh-CN', generatedAt), en: buildAgreementModel(inspection, 'en-US', generatedAt) };
}

function esc(text: string): string {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function paragraphs(text: string, cls = 'ag-p'): string {
  return text
    .split(/\r?\n/)
    .map((line) => line.replace(/\s+$/, ''))
    .filter((line) => line.trim().length > 0)
    .map((line) => `<p class="${cls}">${esc(line)}</p>`)
    .join('');
}

// The scope of services is the approved text, hard-wrapped at the width of its plain-text source; on
// paper its sentences flow to the page and its bullets hang (src/report/flowText.ts — the words are
// untouched, only the source's line breaks stop being paragraph breaks; 2026-09-21).
function scopeParagraphs(text: string): string {
  return flowHardWrappedText(text)
    .map((block) => `<p class="ag-p${block.kind === 'bullet' ? ' ag-bullet' : ''}">${esc(block.text)}</p>`)
    .join('');
}

// No block of the document paints an opaque background of its own: the DEMO watermark is the root's
// background (src/report/watermark.ts) and has to show through every section, clause boxes included.
export const AGREEMENT_CSS = `
.ag{font-family:Inter,'PingFang SC','Hiragino Sans GB','Microsoft YaHei',system-ui,sans-serif;color:#0b1c30;background-color:#fff;font-size:11pt;line-height:1.5;position:relative}
.ag *{box-sizing:border-box}
.ag h1{font-size:20pt;line-height:1.2;margin:0 0 4pt;letter-spacing:-0.01em}
.ag .ag-state{font-size:9.5pt;color:#404943;text-transform:uppercase;letter-spacing:0.05em;margin:0 0 2pt}
.ag .ag-version{font-size:9pt;color:#707973;margin:0 0 10pt}
.ag h2{font-size:13pt;margin:16pt 0 6pt;padding-bottom:3pt;border-bottom:1.5px solid #0f5238;color:#0f5238;letter-spacing:0.02em;text-transform:uppercase}
.ag .ag-kv{border-collapse:collapse;width:100%;margin:2pt 0}
.ag .ag-kv th{text-align:left;font-weight:600;color:#404943;width:30%;padding:3pt 6pt 3pt 0;vertical-align:top;border-bottom:1px solid #e2e8f0}
.ag .ag-kv td{padding:3pt 0;vertical-align:top;border-bottom:1px solid #e2e8f0}
.ag .ag-p{margin:0 0 4pt;white-space:pre-wrap}
.ag .ag-bullet{padding-left:10pt;text-indent:-10pt}
.ag .ag-clause{margin:0 0 8pt;padding:6pt 10pt;border-left:3pt solid #0f5238;background:rgba(232,236,255,0.35)}
.ag .ag-note{font-size:9.5pt;color:#404943;margin:0 0 6pt}
.ag .ag-sigs{display:flex;gap:16pt;flex-wrap:wrap;margin-top:6pt}
.ag .ag-sigbox{flex:1 1 220pt;border:1px solid #e2e8f0;border-radius:8pt;padding:8pt 10pt;min-height:110pt}
.ag .ag-siglabel{font-size:9.5pt;color:#404943;text-transform:uppercase;letter-spacing:0.05em;margin:0 0 4pt}
.ag .ag-sig{display:block;max-width:100%;height:auto}
.ag .ag-sigline{border-top:1px solid #0b1c30;margin-top:6pt;padding-top:3pt;font-size:10pt}
.ag .ag-sigmeta{font-size:9pt;color:#707973;margin:2pt 0 0}
.ag .ag-unsigned{color:#ba1a1a;font-size:10pt}
.ag .ag-demo-banner{border:2px solid #ba1a1a;color:#ba1a1a;font-weight:700;text-align:center;padding:6pt;margin:0 0 10pt;letter-spacing:0.08em;text-transform:uppercase}
.ag .ag-demo-banner p{margin:0}
.ag .ag-basis{font-size:9.5pt;color:#404943;margin:0 0 6pt}
.ag .ag-points{margin:0 0 6pt;padding:0;list-style:none}
.ag .ag-points li{margin:0 0 3pt;padding-left:10pt;text-indent:-10pt}
.ag .ag-consent-box{margin:6pt 0 0;padding:6pt 10pt;border:1px solid #0f5238;border-radius:6pt;font-weight:600}
.ag .ag-withdrawn{font-size:9.5pt;color:#ba1a1a;margin:4pt 0 0}
.ag .ag-ticked{font-size:9.5pt;color:#404943;margin:3pt 0 6pt}
.ag .ag-license{font-size:9.5pt;color:#404943;margin:8pt 0 0}
.ag .ag-reading{margin:4pt 0 0;font-size:10pt;color:#404943}
.ag .ag-reading-label{font-weight:600}
.ag .ag-precedence{margin-top:10pt;font-weight:600}
.ag .ag-lang{margin:0 0 18pt}
.ag .ag-lang + .ag-lang{border-top:2px solid #0f5238;padding-top:14pt}
.ag .ag-lang-tag{display:inline-block;font-size:9pt;font-weight:700;letter-spacing:0.08em;color:#0f5238;border:1px solid #0f5238;border-radius:999px;padding:1pt 8pt;margin:0 0 8pt}
`;

// The root element. A Demo document carries its watermark as the root's background, so it lies under
// every line of text rather than in a blank block of its own.
function documentOpen(model: AgreementModel, watermark: string, extra: { className?: string; attributes?: string } = {}): string {
  const style = model.demo ? ` style="${esc(demoWatermarkStyle(watermark))}"` : '';
  const className = extra.className ? `ag ${extra.className}` : 'ag';
  return `<div class="${className}"${style} data-demo="${model.demo ? 'true' : 'false'}" data-signed="${model.signed ? 'true' : 'false'}" data-service="${model.service}"${extra.attributes ? ` ${extra.attributes}` : ''}>`;
}

// One language's text, from the title to the records-and-retention clause.
function textSections(model: AgreementModel): string {
  const L = model.labels;
  // OD-8: the consent clause is its own section, immediately before the signatures, and the box
  // shows what the client decided. The wording is the approved text, printed as given.
  const consent = model.consent
    ? `<h2>${esc(model.consent.title)}</h2>
<p class="ag-basis">${esc(model.consent.basis)}</p>
${paragraphs(model.consent.body)}
<ul class="ag-points">${model.consent.points.map((point) => `<li>· ${esc(point)}</li>`).join('')}</ul>
<div class="ag-consent-box" data-consent="${model.consent.given ? 'given' : 'not-given'}">${model.consent.given ? '☑' : '☐'} ${esc(model.consent.checkbox)}</div>
${model.consent.givenNote ? `<p class="ag-ticked">${esc(model.consent.givenNote)}</p>` : ''}
${model.consent.withdrawnNote ? `<p class="ag-withdrawn">${esc(model.consent.withdrawnNote)}</p>` : ''}`
    : '';
  // v5: the limitation of liability and claims terms, each opening with its name in bold, then the Client's tick.
  const lead = (point: string) => {
    const match = /^([^.。]+[.。])\s*([\s\S]*)$/.exec(point);
    // an English name is followed by a space, a Chinese one (ending in 。) is not
    return match ? `<strong>${esc(match[1])}</strong>${match[2] ? `${match[1].endsWith('。') ? '' : ' '}${esc(match[2])}` : ''}` : esc(point);
  };
  const claims = model.claims
    ? `<h2>${esc(model.claims.title)}</h2>
<ul class="ag-points ag-claims">${model.claims.points.map((point) => `<li>· ${lead(point)}</li>`).join('')}</ul>
<div class="ag-consent-box" data-claims="${model.claims.given ? 'given' : 'not-given'}">${model.claims.given ? '☑' : '☐'} ${esc(model.claims.checkbox)}</div>
${model.claims.givenNote ? `<p class="ag-ticked">${esc(model.claims.givenNote)}</p>` : ''}`
    : '';
  // v4 §3: the consent to electronic records and signatures, with the Client's own tick and its time.
  const esign = model.esign
    ? `<h2>${esc(model.esign.title)}</h2>
${paragraphs(model.esign.body)}
<ul class="ag-points">${model.esign.points.map((point) => `<li>· ${esc(point)}</li>`).join('')}</ul>
${paragraphs(model.esign.confirm)}
<div class="ag-consent-box" data-esign="${model.esign.given ? 'given' : 'not-given'}">${model.esign.given ? '☑' : '☐'} ${esc(model.esign.checkbox)}</div>
${model.esign.givenNote ? `<p class="ag-ticked">${esc(model.esign.givenNote)}</p>` : ''}`
    : '';
  return `<h1>${esc(model.title)}</h1>
<p class="ag-state">${esc(model.stateLine)}</p>
<p class="ag-version">${esc(model.versionLine)}</p>
<h2>${esc(L.details)}</h2>
<table class="ag-kv">${model.details.map((r) => `<tr><th>${esc(r.key)}</th><td>${esc(r.value)}</td></tr>`).join('')}</table>
<h2>${esc(L.scope)}</h2>
${scopeParagraphs(model.scope)}
<h2>${esc(L.clauses)}</h2>
<p class="ag-note">${esc(model.clauseNote)}</p>
${model.clauses
  .map((c, index) => {
    const reading = model.clauseReadings?.texts[index];
    return `<div class="ag-clause"><p class="ag-p">${esc(c)}</p>${reading && model.clauseReadings ? `<p class="ag-reading"><span class="ag-reading-label">${esc(model.clauseReadings.label)}</span>${esc(reading)}</p>` : ''}</div>`;
  })
  .join('')}
${claims}${consent}
${model.retention ? paragraphs(model.retention) : ''}
${esign}
${model.copies ? paragraphs(model.copies) : ''}
${model.languagePrecedence ? `<p class="ag-p ag-precedence">${esc(model.languagePrecedence)}</p>` : ''}${
    model.clausesOriginal
      ? `
<section class="ag-original" lang="en" data-original="mandatory-clauses">
<h2>${esc(model.clausesOriginal.title)}</h2>
<p class="ag-note">${esc(model.clausesOriginal.note)}</p>
${model.clausesOriginal.texts.map((c) => `<div class="ag-clause"><p class="ag-p">${esc(c)}</p></div>`).join('')}
</section>`
      : ''
  }`;
}

function signatureBox(p: { label: string; name: string; svg: string | null; signedLines: string[]; notSigned: string }): string {
  return `<div class="ag-sigbox">
  <p class="ag-siglabel">${esc(p.label)}</p>
  ${p.svg ?? `<p class="ag-unsigned">${esc(p.notSigned)}</p>`}
  <p class="ag-sigline">${esc(p.name)}</p>
  ${p.signedLines.map((line) => `<p class="ag-sigmeta">${esc(line)}</p>`).join('')}
</div>`;
}

function licenseLine(license: NonNullable<AgreementModel['license']>): string {
  return `<p class="ag-license">${esc(license.label)}: ${esc(license.number)} — ${esc(license.note)}</p>`;
}

// ONE language version as a complete document of its own — from the copy of 2026-10-28 the document the
// client signs. data-text-layer: the English document carries the PDF's invisible text layer; the Chinese
// one does not (Helvetica cannot encode it — the characters stay in the page image only, never garbled).
export function renderAgreementBody(model: AgreementModel): string {
  const L = model.labels;
  const sig = (p: AgreementModel['client']) => signatureBox({ label: p.label, name: p.name, svg: p.svg, signedLines: p.signedAt ? [`${L.signedAt}: ${p.signedAt}`, ...(p.remoteNote ? [p.remoteNote] : [])] : [], notSigned: L.notSigned });
  return `${documentOpen(model, L.demoWatermark, { attributes: `lang="${model.locale === 'zh-CN' ? 'zh-CN' : 'en'}" data-languages="${model.locale}" data-text-layer="${model.locale === 'en-US' ? 'true' : 'false'}"` })}
${model.demo ? `<div class="ag-demo-banner">${esc(L.demoBanner)}</div>` : ''}
${textSections(model)}
<h2>${esc(L.signatures)}</h2>
<div class="ag-sigs">${sig(model.client)}${sig(model.inspector)}</div>
${model.license ? licenseLine(model.license) : ''}
</div>`;
}

// The agreement as it is signed and kept: the Chinese version, then the English version, then ONE
// set of signatures — both parties sign the whole document once, so each box is labelled in both
// languages and dated in both. The watermark carries the Chinese label, which already reads
// "DEMO 演示".
export function renderBilingualAgreementBody(models: BilingualAgreementModels): string {
  const { zh, en } = models;
  const both = (a: string, b: string) => `${a} · ${b}`;
  const sig = (z: AgreementModel['client'], e: AgreementModel['client']) =>
    signatureBox({
      label: both(z.label, e.label),
      name: e.name,
      svg: e.svg,
      signedLines: z.signedAt && e.signedAt ? [`${zh.labels.signedAt}: ${z.signedAt}`, `${en.labels.signedAt}: ${e.signedAt}`, ...(z.remoteNote && e.remoteNote ? [z.remoteNote, e.remoteNote] : [])] : [],
      notSigned: both(zh.labels.notSigned, en.labels.notSigned)
    });
  // data-text-layer: the PDF carries an invisible text layer for what Helvetica can encode — the English
  // version (the one that governs); the Chinese characters stay in the page image only, never garbled.
  return `${documentOpen(en, zh.labels.demoWatermark, { className: 'ag-bilingual', attributes: 'data-languages="zh-CN en-US" data-text-layer="true"' })}
${en.demo ? `<div class="ag-demo-banner"><p>${esc(zh.labels.demoBanner)}</p><p>${esc(en.labels.demoBanner)}</p></div>` : ''}
<section class="ag-lang" lang="zh-CN" data-lang="zh-CN">
<p class="ag-lang-tag">${esc(zh.labels.languageVersion)}</p>
${textSections(zh)}
</section>
<section class="ag-lang" lang="en" data-lang="en-US">
<p class="ag-lang-tag">${esc(en.labels.languageVersion)}</p>
${textSections(en)}
</section>
<h2>${esc(both(zh.labels.signatures, en.labels.signatures))}</h2>
<div class="ag-sigs">${sig(zh.client, en.client)}${sig(zh.inspector, en.inspector)}</div>
${zh.license && en.license ? `${licenseLine(zh.license)}${licenseLine(en.license)}` : ''}
</div>`;
}

export function renderBilingualAgreementHtml(models: BilingualAgreementModels): string {
  return `<style>${AGREEMENT_CSS}</style>${renderBilingualAgreementBody(models)}`;
}

// THE agreement document of an inspection, as every caller shows, prints, fingerprints and sends it. A
// document of the copy of 2026-10-28 or later is in the one language chosen for the client (Owner:
// 「按照客户的语言选择单独出，不用双语」); one signed under an older copy is the bilingual document it was
// signed as, byte for byte (its kept PDF's fingerprint must not move).
export function renderAgreementDocumentBody(inspection: AgreementSource, generatedAt: number): string {
  if (agreementIsSingleLanguage(agreementTextVersion(inspection.agreement))) return renderAgreementBody(buildAgreementModel(inspection, inspection.agreement.documentLanguage, generatedAt));
  return renderBilingualAgreementBody(buildBilingualAgreementModels(inspection, generatedAt));
}

export function renderAgreementDocumentHtml(inspection: AgreementSource, generatedAt: number): string {
  return `<style>${AGREEMENT_CSS}</style>${renderAgreementDocumentBody(inspection, generatedAt)}`;
}
